import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FaqList, { type FaqItem } from "@/components/feature/FaqList";
import { MAIN_SITE_URL } from "@/mocks/categories";

const ARTICLE_IMAGE =
  "https://readdy.ai/api/search-image?query=Overhead%20view%20of%20a%20tidy%20desk%20with%20a%20printed%20product%20category%20checklist%20a%20pencil%20and%20folded%20clothing%20fabric%20swatches%20on%20a%20warm%20sage%20green%20and%20cream%20background%20editorial%20still%20life%20for%20a%20shopping%20guide%20high%20detail&width=1600&height=900&seq=hero-article&orientation=landscape&nocache=true";

export default function SpreadsheetGuide() {
  const { t } = useTranslation();

  const sections = [
    { h: "article.h2.1", p: ["article.p.1", "article.p.2"] },
    { h: "article.h2.2", p: ["article.p.3", "article.p.4"] },
    { h: "article.h2.3", p: ["article.p.5", "article.p.6"] },
    { h: "article.h2.4", p: ["article.p.7", "article.p.8"] },
    { h: "article.h2.5", p: ["article.p.9"] },
  ];

  const faqItems: FaqItem[] = [1, 2, 3, 4].map((i) => ({
    q: t(`article.faq.q${i}`),
    a: t(`article.faq.a${i}`),
  }));

  return (
    <>
      <Navbar />

      <main className="pt-16">
        {/* Hero */}
        <section className="relative w-full overflow-hidden">
          <img
            src={ARTICLE_IMAGE}
            alt={t("article.title")}
            title={t("article.title")}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/60" />
          <div className="relative z-10 px-4 md:px-6 py-20 md:py-28 max-w-3xl mx-auto text-center">
            <nav className="text-xs md:text-sm text-background-50/70 mb-4">
              <Link to="/" className="hover:text-background-50 transition-colors">
                {t("nav.home")}
              </Link>
              <span className="mx-2">/</span>
              <span className="text-background-50/90">{t("nav.guide")}</span>
            </nav>
            <h1 className="text-2xl md:text-4xl font-bold text-background-50 leading-tight tracking-tight">
              {t("article.title")}
            </h1>
            <p className="mt-3 text-xs md:text-sm text-background-50/70">
              {t("article.updated")}
            </p>
          </div>
        </section>

        {/* Body */}
        <article className="px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-3xl mx-auto">
            <p className="text-base md:text-lg text-foreground-800 leading-relaxed">
              {t("article.lead")}
            </p>

            {sections.map((s) => (
              <section key={s.h} className="mt-10">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
                  {t(s.h)}
                </h2>
                {s.p.map((p) => (
                  <p key={p} className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                    {t(p)}
                  </p>
                ))}
              </section>
            ))}

            {/* Internal link back to categories */}
            <div className="mt-10 p-5 rounded-lg bg-accent-100/60 border border-accent-200">
              <p className="text-sm text-foreground-700">
                <i className="ri-links-line mr-1.5 text-accent-700" />
                <Link to="/#categories" className="font-semibold text-accent-800 underline hover:text-accent-900">
                  {t("categories.title")}
                </Link>{" "}
                — {t("article.cta.text")}
              </p>
            </div>

            {/* FAQ */}
            <section className="mt-12">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                {t("faq.title")}
              </h2>
              <FaqList items={faqItems} />
            </section>

            {/* CTA */}
            <section className="mt-12 p-6 md:p-8 rounded-lg bg-primary-500 text-center">
              <h2 className="text-xl md:text-2xl font-bold text-background-50">
                {t("article.cta.title")}
              </h2>
              <p className="mt-3 text-sm md:text-base text-background-50/90">
                {t("article.cta.text")}
              </p>
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50 text-foreground-900 text-sm md:text-base font-semibold whitespace-nowrap hover:bg-background-100 transition-colors"
                >
                  {t("article.cta.button")}
                  <i className="ri-arrow-right-up-line text-base" />
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-600 text-background-50 text-sm md:text-base font-semibold whitespace-nowrap hover:bg-primary-700 transition-colors"
                >
                  {t("article.back")}
                </Link>
              </div>
            </section>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}